/**
 * web/src/components/layout/MobileSidebar.tsx
 * Slide-in drawer for the sm breakpoint (CONTEXT.md D-3.2). Uses Radix
 * Dialog under the hood for a11y (focus trap, Esc-to-close, ARIA),
 * styled as a left-side drawer rather than a centered modal.
 *
 * Behavior:
 *  - Opens via the AppShell hamburger button (sm-only).
 *  - Closes on overlay click, Esc, the in-drawer X button, AND on route
 *    change (so navigating from the drawer doesn't leave it open).
 *  - Wraps the regular <Sidebar /> inside `.lg-force-sidebar` so the
 *    sidebar's `md:hidden lg:block` responsive utilities still render the
 *    full sidebar layout inside the drawer at sm widths.
 *
 * Threat T-05-11-03 / T-05-11-04: Radix Dialog enforces focus trap so
 * background nav isn't tabbable while the drawer is open; the
 * `.lg-force-sidebar` overrides are scoped to the drawer container so the
 * desktop sidebar at lg breakpoints remains untouched.
 */
import { useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';

/** Public props for MobileSidebar. */
export interface MobileSidebarProps {
  /** Drawer open state (controlled by AppShell). */
  open: boolean;
  /** Setter — Radix calls this on overlay click / Esc; the X button calls it directly. */
  onOpenChange: (open: boolean) => void;
}

/**
 * Slide-in drawer wrapping <Sidebar /> for the sm breakpoint.
 *
 * @sideEffects subscribes to React Router's location to auto-close on route change.
 * @idempotent yes — pure render driven by `open`; route-change effect is idempotent.
 */
export function MobileSidebar({ open, onOpenChange }: MobileSidebarProps) {
  const location = useLocation();
  // Close on route change. We intentionally only depend on pathname so a
  // re-render with the same path doesn't toggle the drawer.
  useEffect(() => {
    if (open) onOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-[rgba(9,9,11,0.4)]',
            'data-[state=open]:animate-[fade-in_150ms_ease-out]',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-0 top-0 bottom-0 z-50 w-[280px] max-w-[85vw] bg-side-bg shadow-pop outline-none',
            'data-[state=open]:animate-[slide-in-left_220ms_cubic-bezier(.2,.9,.3,1.2)]',
          )}
          onInteractOutside={() => onOpenChange(false)}
          aria-label="Navigation menu"
        >
          {/* sr-only Title satisfies Radix Dialog's a11y requirement
              without showing a visible heading inside the drawer. */}
          <DialogPrimitive.Title className="sr-only">Navigation menu</DialogPrimitive.Title>
          {/* `lg-force-sidebar` lets the regular Sidebar's responsive
              utilities (md:hidden, md:w-14, etc.) be overridden inside the
              drawer so the full 248px-style layout renders even at sm. */}
          <div className="h-full lg-force-sidebar">
            <Sidebar />
          </div>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => onOpenChange(false)}
            className="absolute top-3 right-3 w-7 h-7 rounded-md grid place-items-center text-side-text-muted hover:bg-side-hover hover:text-side-text outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Icon name="x" size={14} />
          </button>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
